import * as THREE from 'three';
import { newModal } from '@/components/UI/WeaveModal/WeaveModal';
import { useState, useEffect } from 'react';
import { RoomData, SpaceData } from '../SpacePlanningPanel/SpacePlanningTypes';
import { FormaHeader } from '../UI/FormaHeader/FormaHeader';
import { WeaveButton } from '../UI/WeaveButton/WeaveButton';
import { unitConversionService as unitService } from '@/services/unit-conversion.service';
import { useSpacePlanningStore } from '@/store/spacePlanningStore';
import { WorkflowProps } from '@/types/workflow';
import { StatsMap } from '@/workers/viewAnalysis.worker';
import { DailySunlightResult } from '@/types/sunlight';
import { calculateViewScore } from '@/services/score/view';
import { calculateSunlightScore } from '@/services/score/sunlight';
import { useViewScoreStore } from '@/store/viewScoreStore';
import { ViewScoreBreakdown } from '@/services/score/view';
import { SunlightScoreBreakdown } from '@/services/score/sunlight';
import { ViewRecommendation } from '@/components/ViewBreakdown';
import { SunlightRecommendation } from '@/components/SunlightBreakdown';
import styles from './styles.module.scss';
import { useWorkflowStore } from '@/store/workflowStore';
import { WeaveTooltip } from '../UI/WeaveTooltip/WeaveTooltip';
import { HorizonContext } from '@/types/horizonContext';

type SpaceWithRooms = SpaceData & { rooms: RoomData[] };

export type ScoreType = 'view' | 'sunlight';

interface ScoreData {
	totalScore: number;
	breakdown: Record<string, number | string>;
	data?: unknown; // Raw data to attach to room (e.g., sunlight array)
	fullBreakdown?: ViewScoreBreakdown | SunlightScoreBreakdown; // Full breakdown for recommendations
}

interface ScoreModalConfig {
	scoreType: ScoreType;
	roomName?: string;
	roomFillMesh?: THREE.Mesh;
	scoreData: ScoreData | null;
	workflowProps: WorkflowProps;
}

const ModalContent = ({ config }: { config: ScoreModalConfig }) => {
	const { scoreType, roomFillMesh, scoreData, workflowProps } = config;
	const currentStage = useWorkflowStore((state) => state.currentStage);
	const spaces = useSpacePlanningStore((state) => state.spaces);
	const updateRoom = useSpacePlanningStore((state) => state.updateRoom);

	const [availableRooms, setAvailableRooms] = useState<Record<string, SpaceWithRooms> | null>(
		workflowProps.workflowData[2]?.data || null
	);
	const [justAddedRoomId, setJustAddedRoomId] = useState<string | null>(null);

	const getRoomsForSelectedPaths = () => {
		const activeFunctions = (workflowProps.workflowData[2]?.data || {}) as Record<
			string,
			SpaceWithRooms
		>;
		const activeKeys = Object.keys(activeFunctions);
		const filteredSpaces = Object.fromEntries(
			Object.entries(spaces || {}).filter(([key]) => activeKeys.includes(key))
		) as Record<string, SpaceWithRooms>;

		return filteredSpaces;
	};

	useEffect(() => {
		const rooms = getRoomsForSelectedPaths();
		setAvailableRooms(rooms || null);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [spaces]);

	useEffect(() => {
		workflowProps.onDataChange({
			data: availableRooms,
		});
	}, [availableRooms, workflowProps]);

	// Find recommended unit based on roomFillMesh's unit
	const recomendedUnit =
		roomFillMesh?.userData.unit?.id && availableRooms
			? Object.entries(availableRooms).reduce<{
				functionId: string;
				space: SpaceWithRooms;
				room: RoomData;
				index: number;
			} | null>((found, [functionId, space]) => {
				if (found) return found;
				const roomIndex = space.rooms.findIndex(
					(room) => room.id === roomFillMesh.userData.unit.id
				);
				if (roomIndex !== -1) {
					return { functionId, space, room: space.rooms[roomIndex], index: roomIndex };
				}
				return null;
			}, null)
			: null;

	if (!scoreData) {
		return (
			<FormaHeader>
				No {scoreType} data available. Please run {scoreType} analysis first.
			</FormaHeader>
		);
	}

	if (!availableRooms || Object.keys(availableRooms).length === 0) {
		return <FormaHeader>No available rooms found. Please configure rooms first.</FormaHeader>;
	}

	const addScoreToRoom = async (
		room: RoomData,
		space: SpaceData & { rooms: RoomData[] },
		index: number,
		functionId: string
	) => {
		const mergedRoom: RoomData = {
			...room,
			...(scoreData.data ? { [scoreType]: scoreData.data } : {}),
			...(scoreType === 'view' && scoreData.fullBreakdown
				? { viewBreakdown: scoreData.fullBreakdown as ViewScoreBreakdown }
				: {}),
			scoreBreakdown: {
				...room.scoreBreakdown,
				[scoreType]: scoreData.totalScore,
			},
		};

		const updatedRooms = [...space.rooms];
		updatedRooms[index] = mergedRoom;

		const updatedSpace: SpaceData & { rooms: RoomData[] } = {
			...space,
			rooms: updatedRooms,
		};

		setAvailableRooms((prev) => ({
			...prev,
			[functionId]: updatedSpace,
		}));

		updateRoom(functionId, mergedRoom);

		// Set temporary "just added" state
		setJustAddedRoomId(String(room.id));
		setTimeout(() => {
			setJustAddedRoomId(null);
		}, 2000);
	};

	const removeScoreFromRoom = async (
		room: RoomData,
		space: SpaceData & { rooms: RoomData[] },
		index: number,
		functionId: string
	) => {
		const mergedRoom: RoomData = {
			...room,
			[scoreType]: undefined,
			...(scoreType === 'view' ? { viewBreakdown: undefined } : {}),
			scoreBreakdown: {
				...room.scoreBreakdown,
				[scoreType]: undefined,
			},
		};

		const updatedRooms = [...space.rooms];
		updatedRooms[index] = mergedRoom;

		const updatedSpace: SpaceData & { rooms: RoomData[] } = {
			...space,
			rooms: updatedRooms,
		};

		setAvailableRooms((prev) => ({
			...prev,
			[functionId]: updatedSpace,
		}));

		updateRoom(functionId, mergedRoom);
	};

	const getRoomScore = (room: RoomData) => {
		if (
			room.scoreBreakdown &&
			room.scoreBreakdown[scoreType] !== undefined &&
			typeof room.scoreBreakdown[scoreType] === 'number'
		) {
			return scoreType === 'sunlight'
				? room.scoreBreakdown[scoreType]
				: (room.scoreBreakdown[scoreType] as number).toFixed(2);
		}
		return 'N/A';
	};

	const renderScoreBreakdown = () => {
		const entries = Object.entries(scoreData.breakdown);

		return (
			<div className={styles.breakdownContainer}>
				{entries.map(([key, value]) => (
					<span key={key} className={styles.breakdownBadge}>
						<span className={styles.badgeLabel}>{key}:</span>{' '}
						<span className={styles.badgeValue}>{value}</span>
					</span>
				))}
			</div>
		);
	};

	const scoreLabel = scoreType === 'view' ? 'View Score' : 'Sunlight Score';
	const maxScore = scoreType === 'view' ? 100 : 10;

	return (
		<div>
			<FormaHeader>
				{scoreLabel}: {scoreData.totalScore.toFixed(1)}/{maxScore}
			</FormaHeader>
			{renderScoreBreakdown()}

			{scoreData.fullBreakdown && (
				<div className={styles.recommendationSection}>
					<strong className={styles.recommendationLabel}>Recommendation:</strong>
					<div className={styles.recommendationText}>
						{scoreType === 'view' ? (
							<ViewRecommendation scoreData={scoreData.fullBreakdown as ViewScoreBreakdown} />
						) : (
							<SunlightRecommendation scoreData={scoreData.fullBreakdown as SunlightScoreBreakdown} />
						)}
					</div>
				</div>
			)}

			<FormaHeader>Add to Room:</FormaHeader>
			{Object.entries(availableRooms).map(([functionId, space]) => (
				<div key={functionId} className={styles.functionGroup}>
					<strong className={styles.functionName}>
						{space.name ?? functionId}
					</strong>
					<ul className={styles.roomList}>
						{space.rooms.map((room, i) => {
							const isRecommended =
								recomendedUnit &&
								recomendedUnit.functionId === functionId &&
								recomendedUnit.room.id === room.id;

							const isJustAdded = justAddedRoomId === String(room.id);

							const roomItemClass = isRecommended
								? `${styles.roomItem} ${styles.recommended}`
								: styles.roomItem;

							return (
								<li key={room.id} className={roomItemClass}>
									<div className={styles.roomInfo}>
										<strong>
											{room.spaceDescription}
											{isRecommended && (
												<span className={styles.recommendedBadge}>
													(Recommended)
												</span>
											)}
										</strong>
										<div className={styles.roomDetail}>
											ASF:{' '}
											{unitService.getPreferredUnit('area') === 'ft2'
												? unitService.toPreferredUnit(room.ASF, 'area', 'm2').toFixed(0)
												: room.ASF.toFixed(0)}{' '}
											{unitService.getPreferredUnit('area')}
										</div>
										<div className={styles.roomDetail}>
											Current {scoreLabel}: {getRoomScore(room)}
										</div>
									</div>

									<div className={styles.actions}>
										{getRoomScore(room) !== 'N/A' && getRoomScore(room) !== 0 && (
											<div className={styles.scoreBadge}>
												Score: {getRoomScore(room)}
											</div>
										)}
										{!isJustAdded ? (
											<>
												<WeaveTooltip
													text={currentStage !== 'design_development'
														? 'Available in Gate 3'
														: 'Add this window\'s view score to a room'
													}>
													<WeaveButton
														variant="outlined"
														onClick={() => addScoreToRoom(room, space, i, functionId)}
														disabled={currentStage !== 'design_development'}
													>
														Add
													</WeaveButton>
												</WeaveTooltip>
												<WeaveTooltip
													text={currentStage !== 'design_development'
														? 'Available in Gate 3'
														: 'Remove this window\'s view score from a room'
													}>
													<WeaveButton
														variant="flat"
														disabled={
															getRoomScore(room) === 'N/A' || getRoomScore(room) === 0 || currentStage !== 'design_development'
														}
														onClick={() =>
															removeScoreFromRoom(room, space, i, functionId)
														}
													>
														Remove
													</WeaveButton>
												</WeaveTooltip>
											</>
										) : (
											<WeaveButton variant="solid" disabled>
												Added ✓
											</WeaveButton>
										)}
									</div>
								</li>
							);
						})}
					</ul>
				</div>
			))
			}
		</div >
	);
};

export const openScoreModal = (config: ScoreModalConfig) => {
	const title = config.scoreType === 'view' ? 'Add View Score to Room' : 'Add Sunlight Score to Room';

	newModal({
		title,
		content: <ModalContent config={config} />,
		width: '600px',
	});
};

export const handleAddScoreToRoomModal = (
	data: Record<string, StatsMap> | DailySunlightResult[],
	workflowProps: WorkflowProps,
	viewScoreSettings?: { horizonContexts: HorizonContext[]; isBeyondHorizonEnabled: boolean },
	roomFillMesh?: THREE.Mesh
) => {
	const isViewData = !Array.isArray(data);
	if (isViewData) {
		// Handle view data
		const viewData = data as Record<string, StatsMap>;
		const horizonContexts =
			viewScoreSettings?.horizonContexts ?? useViewScoreStore.getState().horizonContexts;
		const isBeyondHorizonEnabled =
			viewScoreSettings?.isBeyondHorizonEnabled ??
			useViewScoreStore.getState().isBeyondHorizonEnabled;
		const viewScore = calculateViewScore(viewData, horizonContexts, isBeyondHorizonEnabled);

		const scoreData = {
			totalScore: viewScore.totalScore,
			breakdown: {
				Open: viewScore.openScore.toFixed(1),
				...(viewScore.beyondHorizonEnabled
					? { 'Beyond Horizon Bonus': viewScore.beyondHorizonBonus.toFixed(1) }
					: {}),
				Vegetation: viewScore.vegetationScore.toFixed(1),
				Water: viewScore.waterScore.toFixed(1),
				'Sky View': viewScore.skyViewFactor.toFixed(1),
				Diversity: viewScore.viewDiversity.toFixed(1),
				Horizontal: viewScore.horizontalOpenness.toFixed(1),
			},
			data: viewData, // Include raw view data to attach to room
			fullBreakdown: viewScore, // Include full breakdown for recommendations
		};

		openScoreModal({
			scoreType: 'view',
			roomFillMesh,
			scoreData,
			workflowProps,
		});
	} else {
		// Handle sunlight data
		const sunlightData = data as DailySunlightResult[];
		const sunlightScore = calculateSunlightScore(sunlightData);

		const scoreData = {
			totalScore: sunlightScore.totalScore,
			breakdown: {
				Winter: sunlightScore.winterScore.toFixed(1),
				Summer: sunlightScore.summerScore.toFixed(1),
				'Spring/Fall': sunlightScore.springFallScore.toFixed(1),
				'Points analyzed': sunlightScore.pointsAnalyzed.toString(),
				'Avg hours': `${sunlightScore.averageHours.toFixed(1)}h/day`,
			},
			data: sunlightData, // Include raw sunlight data to attach to room
			fullBreakdown: sunlightScore, // Include full breakdown for recommendations
		};

		openScoreModal({
			scoreType: 'sunlight',
			scoreData,
			workflowProps,
		});
	}
};
